/**
 * AdminIntegrationCredentialsPanel — Iter 67ak.
 *
 * Form for entering Twilio (WhatsApp) + Google Places API credentials. The
 * backend never returns plaintext, so each input shows the masked current
 * value (e.g. ••••••••2345) and accepts a new value to overwrite. Empty
 * input on save = "leave existing value untouched". A small "Clear" button
 * per field lets the admin explicitly wipe a credential.
 *
 * Status pills at the top tell the admin at-a-glance whether each service is
 * fully wired (`twilio_enabled` / `google_places_enabled`).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, KeyRound, MessageSquare, Globe, ExternalLink, CheckCircle2, AlertCircle, Trash2, Send, Loader2, Mail, CreditCard, Webhook, Plug, Sparkles } from "lucide-react";
import { PlatformPlaceIdCard } from "@/components/admin/PlatformPlaceIdCard";

const FIELDS = [
  {
    section: "twilio",
    key: "twilio_account_sid",
    label: "Twilio Account SID",
    placeholder: "AC...",
    helper: "Twilio Console → Account Info panel.",
    required: true,
  },
  {
    section: "twilio",
    key: "twilio_auth_token",
    label: "Twilio Auth Token",
    placeholder: "Click 'Show' on the Twilio console to reveal",
    helper: "Same panel as the SID.",
    type: "password",
    required: true,
  },
  {
    section: "twilio",
    key: "twilio_whatsapp_from",
    label: "Twilio WhatsApp sender",
    placeholder: "+14155238886",
    helper: "Sandbox uses +14155238886. Production uses your registered WABA number (E.164).",
    required: true,
  },
  {
    section: "twilio",
    key: "admin_whatsapp",
    label: "Admin WhatsApp number",
    placeholder: "+33612345678",
    helper: "Receives a copy of every booking notification. Optional.",
  },
  {
    section: "google",
    key: "google_places_api_key",
    label: "Google Places API Key",
    placeholder: "AIza...",
    helper: "Cloud Console → APIs & Services → Credentials. Enable both 'Places API' and 'Places API (New)'.",
    type: "password",
    required: true,
  },
  // Iter 68 — Stripe credentials (test-mode first, then live).
  {
    section: "stripe",
    key: "stripe_api_key",
    label: "Stripe Secret Key",
    placeholder: "sk_test_… or sk_live_…",
    helper: "Stripe Dashboard → Developers → API keys. Use sk_test_ for test mode end-to-end validation, switch to sk_live_ for production.",
    type: "password",
    required: true,
  },
  {
    section: "stripe",
    key: "stripe_publishable_key",
    label: "Stripe Publishable Key",
    placeholder: "pk_test_… or pk_live_…",
    helper: "Same Stripe page as the secret key. Safe to expose to browsers — used by Stripe.js / Checkout client.",
    required: true,
  },
  {
    section: "stripe",
    key: "stripe_webhook_secret",
    label: "Stripe Webhook Signing Secret",
    placeholder: "whsec_…",
    helper: "Stripe Dashboard → Developers → Webhooks → click your endpoint → Signing secret. Required for booking-payment status updates.",
    type: "password",
  },
  {
    section: "stripe",
    key: "stripe_connect_client_id",
    label: "Stripe Connect Client ID",
    placeholder: "ca_…",
    helper: "Stripe Dashboard → Connect → Settings → OAuth → Client ID. Required for vendor payouts (express/standard accounts).",
  },
];

export default function AdminIntegrationCredentialsPanel() {
  const [creds, setCreds] = useState({});
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Iter 67el — test-send state. `testTo` lets the admin try a custom
  // recipient; empty falls back to the saved admin_whatsapp.
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Iter 67en++ — same pattern for the Resend email pipeline.
  const [emailTo, setEmailTo] = useState("");
  const [emailTesting, setEmailTesting] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integration-credentials");
      setCreds(data || {});
      setEdits({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const status = creds._status || {};

  // Iter 67ak follow-up — surface exactly which required fields are still
  // missing so admins don't squint at the "Inactive" pill wondering why.
  const missingTwilio = FIELDS
    .filter((f) => f.section === "twilio" && f.required && !creds[f.key]?.has_value)
    .map((f) => f.label);
  const missingGoogle = FIELDS
    .filter((f) => f.section === "google" && f.required && !creds[f.key]?.has_value)
    .map((f) => f.label);
  const missingStripe = FIELDS
    .filter((f) => f.section === "stripe" && f.required && !creds[f.key]?.has_value)
    .map((f) => f.label);

  const save = async () => {
    // Only send fields the admin actually edited so we don't overwrite an
    // existing token with empty string by accident.
    const payload = {};
    for (const [k, v] of Object.entries(edits)) {
      if (typeof v === "string") payload[k] = v;
    }
    if (!Object.keys(payload).length) {
      toast("No changes to save");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/integration-credentials", payload);
      toast.success("Credentials saved");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clearOne = async (key) => {
    if (!window.confirm(`Clear ${key}? Outbound notifications using it will stop.`)) return;
    setSaving(true);
    try {
      await api.put("/admin/integration-credentials", { [key]: "" });
      toast.success("Credential cleared");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Iter 67el — one-click test send. Hits the backend test endpoint
  // which surfaces the REAL Twilio error (auth, sender unverified,
  // sandbox opt-in missing, etc.) instead of masking it. Result is
  // pinned underneath the button so the admin can read it without a
  // toast-disappearing race.
  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(
        "/admin/integration-credentials/test-whatsapp",
        { to: testTo.trim() || null }
      );
      setTestResult(data);
      if (data.ok) {
        toast.success(`Test message sent → ${data.to}`);
      } else {
        toast.error(data.error || "Test send failed — see details below");
      }
    } catch (e) {
      const msg = formatApiError(e);
      setTestResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  // Iter 67en++ — Resend test-email one-click smoke test. Mirrors the
  // WhatsApp helper above; surfaces the raw Resend error so the admin
  // can debug DNS/sender/quota issues without reading server logs.
  const [stripeTesting, setStripeTesting] = useState(false);
  const [stripeResult, setStripeResult] = useState(null);
  const [stripeCleaning, setStripeCleaning] = useState(false);
  const [stripeCleanupResult, setStripeCleanupResult] = useState(null);

  // Iter 68 — Stripe one-click smoke test. Creates a €1 PaymentIntent
  // in `requires_payment_method` state, then cancels it — zero charge,
  // zero card, full round-trip validation of the secret key + mode.
  const sendTestStripe = async () => {
    setStripeTesting(true);
    setStripeResult(null);
    try {
      const { data } = await api.post(
        "/admin/integration-credentials/test-stripe",
        {}
      );
      setStripeResult(data);
      if (data.ok) {
        toast.success(`Stripe ${data.mode} mode · PI ${data.payment_intent_id?.slice(0, 18)}…`);
      } else {
        toast.error(data.error || "Stripe test failed — see details below");
      }
    } catch (e) {
      const msg = formatApiError(e);
      setStripeResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setStripeTesting(false);
    }
  };

  // Iter 68c — Cleanup any stray smoke-test PaymentIntents from
  // earlier sessions. Hits the metadata-tagged search so it never
  // touches real customer bookings.
  const cleanupStripeSmokeTests = async () => {
    if (!window.confirm("Cancel ALL outstanding smoke-test PaymentIntents on this Stripe account? Real customer bookings are never touched.")) return;
    setStripeCleaning(true);
    setStripeCleanupResult(null);
    try {
      const { data } = await api.post(
        "/admin/integration-credentials/stripe-cleanup-smoke-tests",
        {}
      );
      setStripeCleanupResult(data);
      if (data.ok) {
        toast.success(`Cleanup complete · ${data.cancelled} cancelled, ${data.skipped_terminal} already terminal`);
      } else {
        toast.error(data.error || "Cleanup failed — see details below");
      }
    } catch (e) {
      const msg = formatApiError(e);
      setStripeCleanupResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setStripeCleaning(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailTesting(true);
    setEmailResult(null);
    try {
      const { data } = await api.post(
        "/admin/integration-credentials/test-email",
        { to: emailTo.trim() || null }
      );
      setEmailResult(data);
      if (data.ok) {
        toast.success(`Test email sent → ${data.to}`);
      } else {
        toast.error(data.error || "Test email failed — see details below");
      }
    } catch (e) {
      const msg = formatApiError(e);
      setEmailResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setEmailTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="admin-credentials-loading">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-neutral-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const renderField = (f) => {
    const meta = creds[f.key] || {};
    const editVal = edits[f.key];
    const editing = editVal !== undefined;
    const showClear = meta.has_value && !editing;
    return (
      <div key={f.key} data-testid={`cred-row-${f.key}`}>
        <Label className="font-medium flex items-center gap-1.5">
          {f.label}
          {f.required && (
            <span className="text-[9px] uppercase tracking-wider font-bold text-rose-600 bg-rose-50 ring-1 ring-rose-100 px-1.5 py-0.5 rounded-full">Required</span>
          )}
          {!f.required && (
            <span className="text-[9px] uppercase tracking-wider font-medium text-neutral-400">Optional</span>
          )}
        </Label>
        <div className="relative mt-1.5">
          <Input
            type={f.type || "text"}
            className="rounded-xl pr-24"
            placeholder={meta.has_value ? `Current: ${meta.masked}` : f.placeholder}
            value={editing ? editVal : ""}
            onChange={(e) => setEdits((s) => ({ ...s, [f.key]: e.target.value }))}
            data-testid={`cred-input-${f.key}`}
          />
          {showClear && (
            <button
              type="button"
              onClick={() => clearOne(f.key)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-neutral-400 hover:text-rose-600 transition flex items-center gap-1"
              title="Clear this credential"
              data-testid={`cred-clear-${f.key}`}
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 mt-1.5">
          <p className="text-[11px] text-neutral-500">{f.helper}</p>
          {meta.has_value && (
            <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${meta.source === "database" ? "text-emerald-700" : "text-neutral-500"}`}>
              {meta.source === "database" ? "Saved" : meta.source === "env" ? "Env var" : ""}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-10" data-testid="admin-integration-credentials">
      {/* ----- Status pills ----- */}
      <div className="flex flex-wrap gap-3" data-testid="admin-credentials-status">
        <StatusPill enabled={status.twilio_enabled} label="WhatsApp (Twilio)" missing={missingTwilio} />
        <StatusPill enabled={status.google_places_enabled} label="Google Places" missing={missingGoogle} />
        <StatusPill
          enabled={status.stripe_enabled}
          label={`Stripe${status.stripe_mode === "test" ? " (Test mode)" : status.stripe_mode === "live" ? " (Live)" : ""}`}
          missing={missingStripe}
        />
      </div>

      {/* ----- Twilio section ----- */}
      <section>
        <header className="mb-5">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-green-100 text-green-700 flex items-center justify-center ring-1 ring-green-200">
              <MessageSquare className="h-5 w-5" />
            </span>
            Twilio (WhatsApp notifications)
          </h3>
          <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
            Outbound WhatsApp messages to vendors, customers, and the admin number on every new booking.
            For testing, use the <a href="https://www.twilio.com/console/sms/whatsapp/learn" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Twilio WhatsApp Sandbox <ExternalLink className="h-3 w-3" /></a>.
            Production requires a Meta-approved WhatsApp Business Account.
          </p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {FIELDS.filter((f) => f.section === "twilio").map(renderField)}
        </div>

        {/* Iter 67el — test-send card. Stays inside the Twilio section so
            admins land on it after pasting credentials, before
            wandering off into Google Places. */}
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5" data-testid="twilio-test-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center ring-1 ring-emerald-200 shrink-0">
              <Send className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-emerald-900">Send a test WhatsApp</h4>
              <p className="text-xs text-emerald-800/80 mt-0.5 leading-relaxed">
                Fire a test message to verify the config without creating a fake booking.
                Leave the field blank to send to the saved admin number; or paste any E.164 number
                (e.g. <span className="font-mono">+33612345678</span>). For the Twilio sandbox the
                recipient must first join by texting the join code to <span className="font-mono">+14155238886</span>.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="Leave blank for saved admin number"
              className="rounded-full h-9 text-sm bg-white flex-1 min-w-[240px]"
              data-testid="twilio-test-to"
            />
            <Button
              type="button"
              onClick={sendTest}
              disabled={testing || !status.twilio_enabled}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="twilio-test-send"
              title={!status.twilio_enabled ? "Save the three required Twilio credentials first" : "Send a test WhatsApp"}
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send test
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                testResult.ok
                  ? "border-emerald-300 bg-white text-emerald-900"
                  : "border-rose-300 bg-rose-50 text-rose-900"
              }`}
              data-testid="twilio-test-result"
            >
              {testResult.ok ? (
                <div className="space-y-0.5">
                  <div className="font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Message accepted by Twilio
                  </div>
                  <div>Recipient: <span className="font-mono">{testResult.to}</span></div>
                  {testResult.sid && (
                    <div>Twilio SID: <span className="font-mono">{testResult.sid}</span></div>
                  )}
                  <div className="text-[11px] text-emerald-700 mt-1">
                    Delivery to the phone can still fail (sandbox opt-in, recipient not on WhatsApp) — check your WhatsApp now.
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> {testResult.error || "Send failed"}
                  </div>
                  {testResult.debug && (
                    <div className="text-[11px] font-mono text-rose-800/80 bg-white/60 rounded-md px-2 py-1 mt-1">
                      sid_set={String(testResult.debug.sid_set)} · token_set={String(testResult.debug.token_set)} · sender_set={String(testResult.debug.sender_set)} · sender_format={testResult.debug.sender_value_format}
                    </div>
                  )}
                  <div className="text-[11px] text-rose-700/80">
                    Common fixes: check the Twilio SID + Auth Token are correct; the sender number must be the format <span className="font-mono">+14155238886</span> (with leading + and country code); the recipient must have joined the WhatsApp sandbox if you're testing.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ----- Resend / Email section (Iter 67en++) -----
          Surfaces a one-click test-send to verify Resend deliverability
          (DNS, sender format, key health). No editable form fields here
          because the Resend key + sender live in backend/.env — the
          card mirrors the Twilio test-card to keep the admin UX
          consistent. */}
      <section>
        <header className="mb-5">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center ring-1 ring-amber-200">
              <Mail className="h-5 w-5" />
            </span>
            Resend (Outbound email)
          </h3>
          <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
            Powers all transactional emails — booking confirmations, password resets, vendor invitations, invoice deliveries.
            Resend key &amp; sender are configured in <code className="font-mono bg-neutral-100 px-1 rounded">backend/.env</code>;
            use the panel below to verify deliverability anytime.
          </p>
        </header>

        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="email-test-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center ring-1 ring-amber-200 shrink-0">
              <Send className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-amber-900">Send a test email</h4>
              <p className="text-xs text-amber-800/80 mt-0.5 leading-relaxed">
                Fire a real "RentalCar.al — test email" via Resend to verify DNS, sender display name, and key health.
                Leave the field blank to send to your own admin address; or paste any email. Use{" "}
                <span className="font-mono">delivered@resend.dev</span> for a guaranteed sandbox send (no inbox required).
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="Leave blank for your admin email"
              className="rounded-full h-9 text-sm bg-white flex-1 min-w-[240px]"
              data-testid="email-test-to"
            />
            <Button
              type="button"
              onClick={sendTestEmail}
              disabled={emailTesting}
              className="rounded-full bg-amber-600 hover:bg-amber-700"
              data-testid="email-test-send"
              title="Send a test email via Resend"
            >
              {emailTesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send test
            </Button>
          </div>
          {emailResult && (
            <div
              className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                emailResult.ok
                  ? "border-amber-300 bg-white text-amber-900"
                  : "border-rose-300 bg-rose-50 text-rose-900"
              }`}
              data-testid="email-test-result"
            >
              {emailResult.ok ? (
                <div className="space-y-0.5">
                  <div className="font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Email accepted by Resend
                  </div>
                  <div>Recipient: <span className="font-mono">{emailResult.to}</span></div>
                  {emailResult.sender && (
                    <div>From: <span className="font-mono">{emailResult.sender}</span></div>
                  )}
                  {emailResult.id && (
                    <div>Resend ID: <span className="font-mono">{emailResult.id}</span></div>
                  )}
                  <div className="text-[11px] text-amber-700 mt-1">
                    Delivery to the inbox can still fail (spam filter, mailbox full). Check the Resend dashboard for the final status.
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> {emailResult.error || "Send failed"}
                  </div>
                  {emailResult.debug && (
                    <div className="text-[11px] font-mono text-rose-800/80 bg-white/60 rounded-md px-2 py-1 mt-1">
                      key_set={String(emailResult.debug.key_set)} · sender_set={String(emailResult.debug.sender_set)} · sender_format={emailResult.debug.sender_format}
                    </div>
                  )}
                  <div className="text-[11px] text-rose-700/80">
                    Common fixes: domain not verified in Resend → check DNS records on resend.com/domains. Sender format must be either{" "}
                    <span className="font-mono">"Name &lt;addr@domain&gt;"</span> or bare <span className="font-mono">addr@domain</span>.
                    Free tier is limited to 100 emails/day.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ----- Stripe section (Iter 68) -----
          Manages the Stripe secret/publishable keys, webhook signing
          secret, and Connect client ID. Test-mode keys (sk_test_) are
          surfaced via a "Test mode" pill so the admin can validate
          end-to-end before switching to sk_live_. Saved values are
          hydrated into `os.environ` on save + startup so existing
          StripeCheckout / Connect / webhook code paths just work. */}
      <section>
        <header className="mb-5">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center ring-1 ring-indigo-200">
              <CreditCard className="h-5 w-5" />
            </span>
            Stripe (Payments &amp; Connect payouts)
          </h3>
          <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
            Powers customer card payments, vendor payouts via Stripe Connect, and platform commission. Start with <strong>test-mode keys</strong>
            (<code className="font-mono bg-neutral-100 px-1 rounded">sk_test_…</code> / <code className="font-mono bg-neutral-100 px-1 rounded">pk_test_…</code>) to
            validate end-to-end, then switch to live. Grab keys at{" "}
            <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
              Stripe Dashboard → Developers → API keys <ExternalLink className="h-3 w-3" />
            </a>.
          </p>
          {/* Capability strip — at-a-glance which Stripe surface is wired. */}
          <div className="flex flex-wrap gap-2 mt-3">
            <CapPill ok={status.stripe_enabled} icon={CreditCard} label="Card payments" />
            <CapPill ok={status.stripe_webhook_enabled} icon={Webhook} label="Webhooks (booking status)" />
            <CapPill ok={status.stripe_connect_enabled} icon={Plug} label="Connect (vendor payouts)" />
            {status.stripe_mode === "test" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                Test mode · sk_test_
              </span>
            )}
            {status.stripe_mode === "live" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                Live mode · sk_live_
              </span>
            )}
          </div>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {FIELDS.filter((f) => f.section === "stripe").map(renderField)}
        </div>

        {/* Iter 68 — One-click Stripe smoke test. Creates a €1
            PaymentIntent then cancels it — zero card, zero charge,
            but a full round-trip against the saved secret key. Way
            less stressful than running a real test booking before
            the admin has any confidence the keys work. */}
        <div className="mt-6 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="stripe-test-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center ring-1 ring-indigo-200 shrink-0">
              <Send className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm text-indigo-900">Send a test PaymentIntent</h4>
              <p className="text-xs text-indigo-800/80 mt-0.5 leading-relaxed">
                Creates a €1.00 PaymentIntent in <code className="font-mono bg-white/70 px-1 rounded">requires_payment_method</code> state and immediately cancels it.
                No card, no capture, no charge — just a full round-trip against your saved secret key so you can confirm{" "}
                <strong>auth, mode (test/live), and account health</strong> before running a real booking.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              type="button"
              onClick={sendTestStripe}
              disabled={stripeTesting || !status.stripe_enabled}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="stripe-test-send"
              title={!status.stripe_enabled ? "Save the Stripe secret key first" : "Create + cancel a €1 test PaymentIntent"}
            >
              {stripeTesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Run Stripe smoke test
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={cleanupStripeSmokeTests}
              disabled={stripeCleaning || !status.stripe_enabled}
              className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="stripe-cleanup-send"
              title={!status.stripe_enabled ? "Save the Stripe secret key first" : "Cancel any stray smoke-test PaymentIntents from earlier sessions"}
            >
              {stripeCleaning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Clean up smoke tests
            </Button>
            {!status.stripe_enabled && (
              <span className="text-[11px] text-indigo-700/70">Save your Stripe Secret Key above first.</span>
            )}
          </div>
          {stripeResult && (
            <div
              className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                stripeResult.ok
                  ? "border-indigo-300 bg-white text-indigo-900"
                  : "border-rose-300 bg-rose-50 text-rose-900"
              }`}
              data-testid="stripe-test-result"
            >
              {stripeResult.ok ? (
                <div className="space-y-0.5">
                  <div className="font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Stripe accepted the PaymentIntent
                  </div>
                  <div>
                    Mode:{" "}
                    <span className={`font-semibold ${stripeResult.mode === "test" ? "text-indigo-700" : "text-emerald-700"}`}>
                      {stripeResult.mode}
                    </span>
                    {" · "}Amount: <span className="font-mono">{stripeResult.amount}</span>
                  </div>
                  <div>PaymentIntent: <span className="font-mono">{stripeResult.payment_intent_id}</span></div>
                  <div>Initial status: <span className="font-mono">{stripeResult.status}</span> → cancel: <span className="font-mono">{stripeResult.cancel_status}</span></div>
                  <div className="text-[11px] text-indigo-700 mt-1">
                    Round-trip succeeded — your Stripe keys are live and the account can accept charges. The PaymentIntent has been cancelled so nothing lingers in the dashboard.
                  </div>

                  {/* Recent real-traffic preview. Empty list = no real bookings have hit Stripe yet (or only smoke-tests have, which we filter out). */}
                  {Array.isArray(stripeResult.recent_payment_intents) && (
                    <div className="mt-3 pt-3 border-t border-indigo-100">
                      <div className="text-[11px] uppercase tracking-wider text-indigo-700/80 font-semibold flex items-center gap-1.5 mb-1.5">
                        Recent real PaymentIntents
                        <span className="text-[10px] font-normal text-indigo-600/70 normal-case tracking-normal">(smoke-tests filtered out)</span>
                      </div>
                      {stripeResult.recent_payment_intents.length === 0 ? (
                        <div className="text-[11px] text-indigo-700/60 italic">
                          No real PaymentIntents yet — once a customer books, it shows up here.
                        </div>
                      ) : (
                        <ul className="space-y-1 text-[11px]" data-testid="stripe-recent-pis">
                          {stripeResult.recent_payment_intents.map((pi) => (
                            <li key={pi.id} className="flex items-center gap-2 font-mono">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                pi.status === "succeeded" ? "bg-emerald-100 text-emerald-700" :
                                pi.status === "canceled" ? "bg-neutral-200 text-neutral-600" :
                                pi.status === "requires_payment_method" || pi.status === "requires_action" ? "bg-amber-100 text-amber-700" :
                                "bg-rose-100 text-rose-700"
                              }`}>
                                {pi.status}
                              </span>
                              <span className="text-indigo-900 font-semibold">{pi.amount_display}</span>
                              <span className="text-indigo-800/70 truncate flex-1">{pi.id}</span>
                              {pi.created && (
                                <span className="text-indigo-700/60 text-[10px] shrink-0">
                                  {new Date(pi.created * 1000).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      <a
                        href={`https://dashboard.stripe.com${stripeResult.mode === "test" ? "/test" : ""}/payments`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:underline mt-2"
                      >
                        Open Stripe Dashboard <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> {stripeResult.error || "Stripe test failed"}
                  </div>
                  {(stripeResult.error_type || stripeResult.error_code) && (
                    <div className="text-[11px] font-mono text-rose-800/80 bg-white/60 rounded-md px-2 py-1 mt-1">
                      type={stripeResult.error_type || "—"} · code={stripeResult.error_code || "—"}
                    </div>
                  )}
                  {stripeResult.debug && (
                    <div className="text-[11px] font-mono text-rose-800/80 bg-white/60 rounded-md px-2 py-1 mt-1">
                      key_set={String(stripeResult.debug.key_set)} · key_format={stripeResult.debug.key_format}
                    </div>
                  )}
                  <div className="text-[11px] text-rose-700/80">
                    Common fixes: paste the FULL secret key (sk_test_… or sk_live_…), not a restricted key (rk_…); make sure the key matches the mode (test keys can only create test PaymentIntents); check the Stripe account is fully activated.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Iter 68c — Cleanup result block. Surfaces the count of
              cancelled smoke-test PIs + any errors, separately from
              the main smoke-test result so the admin sees both at
              the same time after running a cleanup pass. */}
          {stripeCleanupResult && (
            <div
              className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
                stripeCleanupResult.ok
                  ? "border-indigo-300 bg-white text-indigo-900"
                  : "border-rose-300 bg-rose-50 text-rose-900"
              }`}
              data-testid="stripe-cleanup-result"
            >
              {stripeCleanupResult.ok ? (
                <div className="space-y-0.5">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Smoke-test cleanup complete
                  </div>
                  <div>
                    Scanned: <span className="font-mono font-semibold">{stripeCleanupResult.scanned}</span>
                    {" · "}Cancelled: <span className="font-mono font-semibold text-emerald-700">{stripeCleanupResult.cancelled}</span>
                    {" · "}Already terminal: <span className="font-mono text-neutral-600">{stripeCleanupResult.skipped_terminal}</span>
                  </div>
                  {Array.isArray(stripeCleanupResult.samples) && stripeCleanupResult.samples.length > 0 && (
                    <div className="mt-1.5 text-[11px] text-indigo-700/80">
                      Sample cancelled IDs:{" "}
                      {stripeCleanupResult.samples.map((s, i) => (
                        <span key={s.id} className="font-mono">
                          {i > 0 ? ", " : ""}{s.id}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(stripeCleanupResult.errors) && stripeCleanupResult.errors.length > 0 && (
                    <div className="mt-1.5 text-[11px] text-amber-700">
                      {stripeCleanupResult.errors.length} cancel call{stripeCleanupResult.errors.length === 1 ? "" : "s"} failed — see the network panel for details.
                    </div>
                  )}
                  <div className="text-[11px] text-indigo-700 mt-1">
                    Only PaymentIntents tagged <code className="font-mono bg-white/70 px-1 rounded">metadata.source = "rentalcar_admin_health_check"</code> were touched — real customer bookings are never affected.
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5" /> {stripeCleanupResult.error || "Cleanup failed"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50/40 px-4 py-3 text-[12px] text-indigo-900 leading-relaxed">
          <strong>Webhook endpoint:</strong> Point Stripe to{" "}
          <code className="font-mono bg-white/70 px-1.5 py-0.5 rounded">
            {(typeof window !== "undefined" ? window.location.origin : "https://your-domain")}/api/webhook/stripe
          </code>
          {" "}and listen for <code className="font-mono">checkout.session.completed</code>,{" "}
          <code className="font-mono">payment_intent.succeeded</code>,{" "}
          <code className="font-mono">payment_intent.payment_failed</code>. Copy the signing secret back here.
        </div>
      </section>

      {/* ----- Google Places section ----- */}
      <section>
        <header className="mb-5">
          <h3 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center ring-1 ring-blue-200">
              <Globe className="h-5 w-5" />
            </span>
            Google Places (Live host reviews)
          </h3>
          <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
            Pulls live Google Business Profile reviews for vendors who have supplied their Place ID.
            Get an API key at <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">Google Cloud Console <ExternalLink className="h-3 w-3" /></a>;
            enable <strong>both</strong> "Places API" and "Places API (New)". Reviews are cached server-side for 6 hours so you stay well within the free quota.
          </p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          {FIELDS.filter((f) => f.section === "google").map(renderField)}
        </div>

        {/* Iter 75 — the platform's own Google Business Profile Place ID,
            powering the homepage "Official Google rating" block. */}
        <PlatformPlaceIdCard />
      </section>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Button
          onClick={save}
          disabled={saving || !Object.keys(edits).length}
          className="rounded-full bg-gradient-to-br from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-200 px-8 disabled:opacity-50"
          data-testid="admin-credentials-save"
        >
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : `Save ${Object.keys(edits).length || ""} change${Object.keys(edits).length === 1 ? "" : "s"}`}
        </Button>
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/40 px-5 py-4 text-xs text-blue-900 flex items-start gap-3">
        <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          Credentials are stored encrypted-at-rest in the database and <strong>never returned in plaintext</strong>. The form only shows masked previews. Saved values <strong>override</strong> any matching values in <code className="font-mono bg-blue-100 px-1 rounded">backend/.env</code> — clearing a field falls back to the env value (if set) or disables that integration.
        </div>
      </div>
    </div>
  );
}

function StatusPill({ enabled, label, missing = [] }) {
  // When inactive AND we know which fields are missing, surface that in the
  // pill itself. Saves the admin from scrolling/scanning rows to find the gap.
  const inactiveDetail = !enabled && missing.length > 0
    ? ` · missing ${missing.join(" + ")}`
    : "";
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${enabled ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`}
      data-testid={`status-pill-${label.toLowerCase().replace(/\s/g, "-")}`}
      title={inactiveDetail.replace(/^ · /, "")}
    >
      {enabled ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
      {label} · {enabled ? "Live" : "Inactive"}{inactiveDetail}
    </span>
  );
}

function CapPill({ ok, icon: Icon, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
        ok
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-neutral-50 text-neutral-500 ring-1 ring-neutral-200"
      }`}
      data-testid={`stripe-cap-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
    >
      <Icon className="h-3 w-3" />
      {label}
      {ok ? <CheckCircle2 className="h-3 w-3 opacity-70" /> : <AlertCircle className="h-3 w-3 opacity-70" />}
    </span>
  );
}

